import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { Icons, PRODUCT_ICON_MAP, classifyTraveler, formatCurrency } from "./bookingConstants";
import styles from "./AddBooking.module.css";
import s from "./StepPricing.module.css";
import {
  ProductCancellationOverview, CancellationSummaryCard,
  CancellationStatusPill, CancelledDirectionBlock,
} from "./CancellationPricing";
import cs from "./CancellationPricing.module.css";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const TYPE_CLS = { ADT: s.typeADT, CHD: s.typeCHD, INF: s.typeINF, "—": s.typeUnknown };
const ICON_BG  = {
  Flight: s.groupIconFlight,
  Hotel: s.groupIconHotel,
  Transfer: s.groupIconTransfer,
  Insurance: s.groupIconInsurance,
};

const PAX_TYPE_MAP = { Adult: "ADT", Child: "CHD", Infant: "INF" };

const GENDER_MAP = {
  MR: "MR", MRS: "MRS", MS: "MS", MISS: "MISS",
  MSTR: "BOY", BOY: "BOY", GIRL: "GIRL",
};

function getGenderLabel(traveler, paxType) {
  if (traveler.title) {
    const key = traveler.title.toUpperCase();
    if (GENDER_MAP[key]) return GENDER_MAP[key];
  }
  if (traveler.gender) {
    const g = traveler.gender.toUpperCase();
    if (paxType === "CHD" || paxType === "INF") return g === "MALE" || g === "M" ? "BOY" : "GIRL";
    return g === "MALE" || g === "M" ? "MR" : "MRS";
  }
  return "—";
}

function formatDOB(dob) {
  if (!dob) return "—";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dob)) return dob;
  const parts = dob.split("-");
  if (parts.length === 3) {
    const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
  }
  return dob;
}

function formatDateShort(d) {
  if (!d) return "—";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return d;
}

function incrementTicket(base, offset) {
  if (!base || offset === 0) return base;
  const match = base.match(/^(.*?)(\d+)$/);
  if (!match) return base;
  const prefix = match[1];
  const numStr = match[2];
  const incremented = parseInt(numStr, 10) + offset;
  return prefix + String(incremented).padStart(numStr.length, "0");
}

const defaultPricingEntry = () => ({
  netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0,
  clientPrice: 0, costPrice: 0, sellingPrice: 0, markup: 0,
  ticketNumber: "",
});

/* Normalize hotel rooms — supports new rooms[] array OR legacy numberOfRooms */
function getHotelRooms(product) {
  if (Array.isArray(product.rooms) && product.rooms.length > 0) return product.rooms;
  const n = Math.max(1, parseInt(product.numberOfRooms, 10) || 1);
  return Array.from({ length: n }, (_, i) => ({
    id: `legacy-${product.id}-${i}`,
    roomType: product.roomType,
    roomName: product.roomName,
    boardName: product.boardName,
    boardCode: product.boardCode,
    roomRate: product.roomRate,
    roomCharacteristic: product.roomCharacteristic,
    roomStatus: product.roomStatus,
    numberOfRooms: 1,
  }));
}

/* ─── Formatted Number Input (European: 1.234,56) ───────────────────────── */

const NumberInput = ({ value, onChange, placeholder = "0,00", className }) => {
  const [focused, setFocused] = useState(false);
  const [local, setLocal] = useState("");

  useEffect(() => {
    if (!focused) setLocal(value == null || value === "" ? "" : String(value));
  }, [value, focused]);

  const display = (() => {
    if (focused) return local;
    if (local === "" || isNaN(parseFloat(local))) return "";
    return parseFloat(local).toLocaleString("nl-BE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  })();

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9.,\-]/g, "");
    v = v.replace(/,/g, ".");
    const parts = v.split(".");
    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
    setLocal(v);
    onChange(v);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={display}
      onFocus={(e) => {
        setFocused(true);
        const num = parseFloat(value);
        if (value == null || value === "" || num === 0) {
          setLocal("");
        } else {
          setLocal(String(value));
          setTimeout(() => e.target.select(), 0);
        }
      }}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(local);
        onChange(isNaN(n) ? "" : String(n));
      }}
      onChange={handleChange}
    />
  );
};

/* ─── Inline SVG Icons ───────────────────────────────────────────────────── */

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
);

const SSRIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
);

const BedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"/><path d="M2 11v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7"/><path d="M7 9h10v2H7z"/></svg>
);

const TrendUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
);

const TrendDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
);

/* ─── Copiable Text ────────────────────────────────────────────────────── */

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
);

const CopyableText = ({ text, className }) => {
  const [copied, setCopied] = useState(false);
  if (!text || text === "—") return <span className={className}>{text || "—"}</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span className={`${s.copiable} ${className || ""}`} onClick={handleCopy} title="Click to copy">
      {text}
      <span className={s.copyBtn}>{copied ? <span className={s.copiedTick}>&#10003;</span> : <CopyIcon />}</span>
    </span>
  );
};

/* ─── Flight Pricing Table ───────────────────────────────────────────────── */

const formatVatBadge = (meta) => {
  if (!meta?.vat_mode) return null;
  if (meta.vat_mode === "NO_VAT" || meta.vat_mode === "AGENT") return "No VAT";
  if (meta.vat_mode === "TOMS") return "TOMS";
  if (meta.vat_mode === "REVERSE_CHARGE") return "Rev. Charge";
  if (meta.vat_mode === "STANDARD") return `${meta.vat_rate}%`;
  return meta.vat_mode;
};

// VAT recalculates on every price keystroke. The cell keeps showing the last
// good amount + badge while a fresh request is in flight (just dimmed), so
// the table row never jumps and the badge doesn't flicker. Once the new
// result arrives the refs catch up and the value crossfades in.
const VATDisplay = ({ value, meta, loading }) => {
  const lastValueRef = useRef(value);
  const lastMetaRef = useRef(meta);
  if (!loading) {
    if (value != null) lastValueRef.current = value;
    if (meta) lastMetaRef.current = meta;
  }
  const dispValue = loading ? lastValueRef.current : value;
  const dispMeta = loading ? lastMetaRef.current : meta;
  const badgeText = formatVatBadge(dispMeta);

  return (
    <div className={s.vatDisplay}>
      <span className={`${s.vatValue} ${loading ? s.vatLoading : ""}`}>
        {formatCurrency(dispValue || 0)}
      </span>
      <span
        className={`${s.vatModeBadge} ${!badgeText ? s.vatModeBadgeEmpty : ""} ${loading ? s.vatModeBadgeLoading : ""}`}
        title={dispMeta?.reason || ""}
        aria-hidden={!badgeText}
      >
        {badgeText || "—"}
      </span>
    </div>
  );
};

const FlightPricingTable = ({ travelers, assignments, pricing, productId, onChange, onCopyToAll, onAutoFillTickets, directionLabel, vatMeta, vatLoading }) => {
  const tis = [...new Set(
    assignments.filter((a) => a.productId === productId).map((a) => a.travelerIndex)
  )];

  const getKey = (ti) => directionLabel ? `${ti}-${productId}-${directionLabel}` : `${ti}-${productId}`;

  const handleCopyToAll = (sourceTi) => {
    if (!onCopyToAll || tis.length <= 1) return;
    const sourceKey = getKey(sourceTi);
    const targetKeys = tis.filter((other) => other !== sourceTi).map((other) => getKey(other));
    onCopyToAll(sourceKey, targetKeys, ["netPrice", "tax", "commission", "segFee"]);
  };

  const handleTicketChange = (ti, value) => {
    const idx = tis.indexOf(ti);
    if (idx === 0 && tis.length > 1) {
      onAutoFillTickets(productId, directionLabel, value, tis);
    } else {
      onChange(getKey(ti), "ticketNumber", value);
    }
  };

  const totals = tis.reduce((acc, ti) => {
    const p = pricing[getKey(ti)] || {};
    acc.netPrice    += p.netPrice    || 0;
    acc.tax         += p.tax         || 0;
    acc.vatTax      += p.vatTax      || 0;
    acc.commission  += p.commission  || 0;
    acc.segFee      += p.segFee      || 0;
    acc.clientPrice += p.clientPrice || 0;
    return acc;
  }, { netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0, clientPrice: 0 });

  if (tis.length === 0) return (
    <div className={s.emptyTableMsg}>
      <div className={s.emptyTableIcon}><InfoIcon /></div>
      <span>No travelers assigned to this product yet.</span>
    </div>
  );

  return (
    <div className={s.flightTable}>
      <div className={s.flightTableHead}>
        <span className={s.fth}>#</span>
        <span className={s.fth}>TYPE</span>
        <span className={s.fth}>TRAVELER</span>
        <span className={s.fth}>GENDER</span>
        <span className={s.fth}>DOB</span>
        <span className={s.fth}>TICKET NUMBER</span>
        <span className={`${s.fth} ${s.fthRight}`}>NET PRICE</span>
        <span className={`${s.fth} ${s.fthRight}`}>TAX</span>
        <span className={`${s.fth} ${s.fthRight}`}>VAT TAX</span>
        <span className={`${s.fth} ${s.fthRight}`}>COMMISSION</span>
        <span className={`${s.fth} ${s.fthRight}`}>SEG. FEE</span>
        <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>CLIENT PRICE</span>
        <span className={s.fth} />
      </div>

      <div className={s.flightTableBody}>
        {tis.map((ti, idx) => {
          const t = travelers[ti];
          if (!t) return null;
          const key        = getKey(ti);
          const p          = pricing[key] || {};
          const paxCat     = PAX_TYPE_MAP[classifyTraveler(t.dob)] || "ADT";
          const genderLbl  = getGenderLabel(t, paxCat);
          const name       = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ") || `Traveler ${ti + 1}`;
          const dob        = formatDOB(t.dob);

          return (
            <div key={ti} className={s.flightRow}>
              <div className={s.fCell}>
                <span className={s.rowNum}>{idx + 1}</span>
              </div>
              <div className={s.fCell}>
                <span className={`${s.cellType} ${TYPE_CLS[paxCat] || s.typeUnknown}`}>{paxCat}</span>
              </div>
              <div className={`${s.fCell} ${s.fCellName}`}>
                <span className={s.travIndex}>{ti + 1}</span>
                <span className={s.travName}>{name}</span>
              </div>
              <div className={s.fCell}>
                <span className={s.genderPill}>{genderLbl}</span>
              </div>
              <div className={s.fCell}>
                <span className={s.dobText}>{dob}</span>
              </div>
              <div className={s.fCell}>
                <input
                  className={`${s.fInput} ${s.ticketInput}`}
                  type="text"
                  placeholder={idx === 0 ? "Master ticket no." : "Auto-filled"}
                  value={p.ticketNumber || ""}
                  onChange={(e) => handleTicketChange(ti, e.target.value)}
                />
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.netPrice ?? ""} onChange={(v) => onChange(key, "netPrice", v)} />
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.tax ?? ""} onChange={(v) => onChange(key, "tax", v)} />
              </div>
              <div className={s.fCell}>
                <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.commission ?? ""} onChange={(v) => onChange(key, "commission", v)} />
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.segFee ?? ""} onChange={(v) => onChange(key, "segFee", v)} />
              </div>
              <div className={s.fCell}>
                <span className={s.clientPriceVal}>{formatCurrency(p.clientPrice || 0)}</span>
              </div>
              <div className={s.fCell}>
                {tis.length > 1 && (
                  <button
                    type="button"
                    className={s.copyToAllBtn}
                    onClick={() => handleCopyToAll(ti)}
                    title="Copy this traveler's prices to all others"
                  >
                    {Icons.copy}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={s.flightTotalsRow}>
        <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 7" }}>
          {directionLabel ? `${directionLabel.toUpperCase()} TOTAL` : "TOTAL"}
        </span>
        <span className={s.flightTotalVal}>{formatCurrency(totals.netPrice)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(totals.tax)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(totals.vatTax)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(totals.commission)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(totals.segFee)}</span>
        <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(totals.clientPrice)}</span>
        <span />
      </div>
    </div>
  );
};

/* ─── Generic Pricing Table (Transfer / Insurance / etc.) ───────────────── */

const PricingTable = ({ travelers, assignments, pricing, productId, onChange, vatMeta, vatLoading }) => {
  const tis = [...new Set(assignments.filter((a) => a.productId === productId).map((a) => a.travelerIndex))];
  const getKey = (ti) => `${ti}-${productId}`;

  const subtotals = tis.reduce((acc, ti) => {
    const p = pricing[getKey(ti)] || {};
    acc.cost    += p.costPrice    || 0;
    acc.selling += p.sellingPrice || 0;
    acc.vatTax  += p.vatTax       || 0;
    return acc;
  }, { cost: 0, selling: 0, vatTax: 0 });
  subtotals.markup = subtotals.selling - subtotals.cost;

  // Single product-level pricing (e.g. Car Rental) — a product with no per-traveler
  // assignment is still priced as one line, like a Transfer. Uses the same fixed grid
  // so its money columns stay aligned with every other product table.
  if (tis.length === 0) {
    const singleKey = `single-${productId}`;
    const sp = pricing[singleKey] || { costPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };
    return (
      <div className={s.table}>
        <div className={s.tableHead}>
          <span className={s.fth} style={{ gridColumn: "1 / 4" }}>Product</span>
          <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
          <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
          <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
          <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
        </div>
        <div className={s.tableBody}>
          <div className={s.row}>
            <div className={`${s.fCell} ${s.fCellName}`} style={{ gridColumn: "1 / 4" }}>
              <span className={s.travName}>Whole product price</span>
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={sp.costPrice ?? ""} onChange={(v) => onChange(singleKey, "costPrice", v)} />
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={sp.markup ?? ""} onChange={(v) => onChange(singleKey, "markup", v)} />
            </div>
            <div className={s.fCell}>
              <VATDisplay value={sp.vatTax} meta={vatMeta?.[singleKey]} loading={vatLoading?.[singleKey]} />
            </div>
            <div className={s.fCell}>
              <span className={s.clientPriceVal}>{formatCurrency(sp.sellingPrice || 0)}</span>
            </div>
          </div>
        </div>
        <div className={s.subtotalRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Subtotal</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.costPrice || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.markup || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.vatTax || 0)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(sp.sellingPrice || 0)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={s.table}>
      <div className={s.tableHead}>
        <span className={s.fth}>Traveler</span>
        <span className={s.fth}>Type</span>
        <span className={s.fth}>Gender</span>
        <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
        <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
        <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
        <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
      </div>
      <div className={s.tableBody}>
        {tis.map((ti) => {
          const t = travelers[ti];
          if (!t) return null;
          const key    = getKey(ti);
          const p      = pricing[key] || { costPrice: 0, sellingPrice: 0, markup: 0, vatTax: 0 };
          const name   = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ") || `Traveler ${ti + 1}`;
          const type   = classifyTraveler(t.dob);
          return (
            <div key={ti} className={s.row}>
              <div className={`${s.fCell} ${s.fCellName}`}>
                <span className={s.cellNameIndex}>{ti + 1}</span>
                <span className={s.travName}>{name}</span>
              </div>
              <div className={s.fCell}>
                <span className={`${s.cellType} ${TYPE_CLS[type] || s.typeUnknown}`}>{type}</span>
              </div>
              <div className={s.fCell}>
                <span className={s.cellGender}>{t.gender || "—"}</span>
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
              </div>
              <div className={s.fCell}>
                <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
              </div>
              <div className={s.fCell}>
                <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
              </div>
              <div className={s.fCell}>
                <span className={s.clientPriceVal}>{formatCurrency(p.sellingPrice)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className={s.subtotalRow}>
        <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Subtotal</span>
        <span className={s.flightTotalVal}>{formatCurrency(subtotals.cost)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(subtotals.markup)}</span>
        <span className={s.flightTotalVal}>{formatCurrency(subtotals.vatTax)}</span>
        <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(subtotals.selling)}</span>
      </div>
    </div>
  );
};

/* ─── Hotel Pricing Group (Per-Room, reads product.rooms[]) ─────────────── */

const HotelPricingGroup = ({ product, pricing, onChange, onResetPricing, accommodationIndex, vatMeta, vatLoading, canc }) => {
  const rooms = getHotelRooms(product);

  const getRoomKey = (room) => `hotelroom-${product.id}-${room.id}`;

  const totals = rooms.reduce((acc, room) => {
    const p = pricing[getRoomKey(room)] || {};
    acc.net     += p.netPrice    || 0;
    acc.markup  += p.markup      || 0;
    acc.vat     += p.vatTax      || 0;
    acc.selling += p.sellingPrice|| 0;
    return acc;
  }, { net: 0, markup: 0, vat: 0, selling: 0 });

  return (
    <div className={s.groupCard}>
      <div className={s.groupHeader}>
        <div className={`${s.groupIcon} ${s.groupIconHotel}`}><BedIcon /></div>
        <div className={s.groupHeaderMain}>
          <div className={s.groupTitle}>
            Accommodation {accommodationIndex}
            {product.hotelName && <span className={s.groupTitleSub}> — {product.hotelName}</span>}
            {product.supplier && !product.hotelName && <span className={s.groupTitleSub}> — {product.supplier}</span>}
            {product.supplierRef && <span className={s.groupTitleSub}> · REF: <CopyableText text={product.supplierRef} /></span>}
          </div>
          <div className={s.groupSub}>
            {product.supplier && <>{product.supplier}</>}
            {product.supplier && <> · </>}
            Check-in {formatDateShort(product.checkIn)} · Check-out {formatDateShort(product.checkOut)} · {rooms.length} room{rooms.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className={s.groupHeaderRight}>
          {canc && <CancellationStatusPill status={canc.status} />}
          <span className={s.badge}>Per-Room Pricing</span>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <ProductCancellationOverview canc={canc} />

      <div className={s.hotelInfoNote}>
        <InfoIcon />
        <span>Room price is not per traveler — it is per room.</span>
      </div>

      {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
      <div className={`${s.hotelTable} ${canc?.cancelledAll ? cs.cancelledTableWrap : ""}`}>
        <div className={s.hotelTableHead}>
          <span className={s.fth}>Room</span>
          <span className={s.fth}>Description</span>
          <span className={`${s.fth} ${s.fthRight}`}>Room Net Price</span>
          <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
          <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
          <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
        </div>

        <div className={s.hotelTableBody}>
          {rooms.map((room, idx) => {
            const key = getRoomKey(room);
            const p   = pricing[key] || { netPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };

            const primaryParts = [room.roomType, room.boardName].filter(Boolean);
            const subParts     = [room.roomName, room.roomCharacteristic, room.roomRate].filter(Boolean);

            return (
              <div key={room.id || idx} className={s.hotelRow}>
                <div className={s.fCell}>
                  <span className={s.roomBadge}>Room {idx + 1}</span>
                </div>
                <div className={`${s.fCell} ${s.hotelDescCell}`}>
                  <span className={s.roomDescPrimary}>
                    {primaryParts.length > 0 ? primaryParts.join(" · ") : "Room"}
                  </span>
                  {subParts.length > 0 && (
                    <span className={s.roomDescSub}>{subParts.join(" · ")}</span>
                  )}
                </div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.netPrice ?? ""} onChange={(v) => onChange(key, "netPrice", v)} />
                </div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
                </div>
                <div className={s.fCell}>
                  <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
                </div>
                <div className={s.fCell}>
                  <span className={s.clientPriceVal}>{formatCurrency(p.sellingPrice || 0)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={s.hotelTotalsRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 3" }}>Accommodation Total</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.net)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.markup)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.vat)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(totals.selling)}</span>
        </div>
      </div>

      <CancellationSummaryCard canc={canc} />
    </div>
  );
};

/* ─── Transfer Pricing Group (Fixed price per product) ──────────────────── */

const TransferPricingGroup = ({ product, pricing, onChange, onResetPricing, vatMeta, vatLoading, canc }) => {
  const key = `transfer-${product.id}`;
  const p = pricing[key] || { costPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };

  const isRoundTrip = product.transferMoment === "RoundTrip";
  const arrLeg = product.arrivalLeg || {};
  const depLeg = product.departureLeg || {};

  const getRoute = (pickup, dropoff) => [pickup, dropoff].filter(Boolean).join(" → ");
  const route = isRoundTrip ? null : getRoute(product.pickupLocation, product.dropoffLocation);
  const arrRoute = isRoundTrip ? getRoute(arrLeg.pickupLocation, arrLeg.dropoffLocation) : null;
  const depRoute = isRoundTrip ? getRoute(depLeg.pickupLocation, depLeg.dropoffLocation) : null;

  const momentLabel = product.transferMoment === "RoundTrip" ? "Round Trip"
    : product.transferMoment === "Arrival" ? "Arrival Transfer"
    : product.transferMoment === "Departure" ? "Departure Transfer"
    : product.transferMoment === "Custom" ? "Custom Transfer"
    : product.transferMoment || "";

  const flightInfo = (leg) => {
    const code = [leg.flightAirlineCode, leg.flightFlightNumber].filter(Boolean).join("");
    if (!code) return null;
    return `${code}${leg.flightDate ? ` · ${formatDateShort(leg.flightDate)}` : ""}${leg.flightTime ? ` · ${leg.flightTime}` : ""}`;
  };

  const paxInfo = [
    product.numberOfAdults ? `${product.numberOfAdults} Adult${product.numberOfAdults > 1 ? "s" : ""}` : null,
    product.numberOfChildren ? `${product.numberOfChildren} Child${product.numberOfChildren > 1 ? "ren" : ""}` : null,
  ].filter(Boolean).join(", ");

  return (
    <div className={s.groupCard}>
      <div className={s.groupHeader}>
        <div className={`${s.groupIcon} ${s.groupIconTransfer}`}>{PRODUCT_ICON_MAP.Transfer || Icons.box}</div>
        <div className={s.groupHeaderMain}>
          <div className={s.groupTitle}>
            Transfer
            {momentLabel && <span className={s.groupTitleSub}> — {momentLabel}</span>}
            {product.supplier && <span className={s.groupTitleSub}> — {product.supplier}</span>}
            {product.supplierRef && <span className={s.groupTitleSub}> · REF: <CopyableText text={product.supplierRef} /></span>}
          </div>
          <div className={s.groupSub}>
            {product.supplier && <>{product.supplier} · </>}
            {product.transferType && <>{product.transferType} · </>}
            {product.vehicleType && <>Private {product.vehicleType} · </>}
            {paxInfo && <>{paxInfo} · </>}
            {product.leadTravelerName && <>Lead: {product.leadTravelerName}</>}
          </div>
        </div>
        <div className={s.groupHeaderRight}>
          {canc && <CancellationStatusPill status={canc.status} />}
          <span className={s.badge}>Fixed Price</span>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <ProductCancellationOverview canc={canc} />

      {/* Transfer Details */}
      <div className={s.transferDetailsPanel}>
        {isRoundTrip ? (
          <div className={s.transferLegsGrid}>
            {/* Arrival Leg */}
            <div className={s.transferLeg}>
              <div className={s.transferLegHeader}>
                <span className={s.transferLegBadge} style={{ background: "#dcfce7", color: "#166534" }}>ARRIVAL</span>
              </div>
              <div className={s.transferLegMeta}>
                {arrRoute && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Route</span><span className={s.transferMetaValue}>{arrRoute}</span></div>}
                {arrLeg.pickupDate && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Date</span><span className={s.transferMetaValue}>{formatDateShort(arrLeg.pickupDate)}</span></div>}
                {(arrLeg.pickupTimeType || "exact") === "exact" && arrLeg.pickupTime && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue}>{arrLeg.pickupTime}</span></div>}
                {arrLeg.pickupTimeType === "tba" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>To Be Advised</span></div>}
                {arrLeg.pickupTimeType === "voucher" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>Follow Voucher</span></div>}
                {flightInfo(arrLeg) && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Flight</span><span className={s.transferMetaValue}>{flightInfo(arrLeg)}</span></div>}
              </div>
            </div>
            {/* Departure Leg */}
            <div className={s.transferLeg}>
              <div className={s.transferLegHeader}>
                <span className={s.transferLegBadge} style={{ background: "#fef3c7", color: "#92400e" }}>DEPARTURE</span>
              </div>
              <div className={s.transferLegMeta}>
                {depRoute && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Route</span><span className={s.transferMetaValue}>{depRoute}</span></div>}
                {depLeg.pickupDate && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Date</span><span className={s.transferMetaValue}>{formatDateShort(depLeg.pickupDate)}</span></div>}
                {(depLeg.pickupTimeType || "exact") === "exact" && depLeg.pickupTime && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue}>{depLeg.pickupTime}</span></div>}
                {depLeg.pickupTimeType === "tba" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>To Be Advised</span></div>}
                {depLeg.pickupTimeType === "voucher" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>Follow Voucher</span></div>}
                {flightInfo(depLeg) && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Flight</span><span className={s.transferMetaValue}>{flightInfo(depLeg)}</span></div>}
              </div>
            </div>
          </div>
        ) : (
          <div className={s.transferLegMeta}>
            {route && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Route</span><span className={s.transferMetaValue}>{route}</span></div>}
            {product.pickupDate && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Date</span><span className={s.transferMetaValue}>{formatDateShort(product.pickupDate)}</span></div>}
            {(product.pickupTimeType || "exact") === "exact" && product.pickupTime && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue}>{product.pickupTime}</span></div>}
            {product.pickupTimeType === "tba" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>To Be Advised</span></div>}
            {product.pickupTimeType === "voucher" && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Pickup Time</span><span className={s.transferMetaValue} style={{ fontStyle: "italic" }}>Follow Voucher</span></div>}
            {product.vehicleType && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Vehicle</span><span className={s.transferMetaValue}>Private {product.vehicleType}</span></div>}
            {flightInfo(product) && <div className={s.transferMetaItem}><span className={s.transferMetaLabel}>Flight</span><span className={s.transferMetaValue}>{flightInfo(product)}</span></div>}
          </div>
        )}
      </div>

      <div className={s.hotelInfoNote}>
        <InfoIcon />
        <span>Transfer price is fixed per product — not per traveler.</span>
      </div>

      {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
      <div className={`${s.hotelTable} ${canc?.cancelledAll ? cs.cancelledTableWrap : ""}`}>
        <div className={s.hotelTableHead}>
          <span className={s.fth}>Type</span>
          <span className={s.fth}>Description</span>
          <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
          <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
          <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
          <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
        </div>

        <div className={s.hotelTableBody}>
          <div className={s.hotelRow}>
            <div className={s.fCell}>
              <span className={s.roomBadge}>{product.transferType || "Transfer"}</span>
            </div>
            <div className={`${s.fCell} ${s.hotelDescCell}`}>
              <span className={s.roomDescPrimary}>{isRoundTrip ? "Round Trip" : (route || "—")}</span>
              {product.vehicleType && <span className={s.roomDescSub}>Private {product.vehicleType}</span>}
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
            </div>
            <div className={s.fCell}>
              <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
            </div>
            <div className={s.fCell}>
              <span className={s.clientPriceVal}>{formatCurrency(p.sellingPrice || 0)}</span>
            </div>
          </div>
        </div>

        <div className={s.hotelTotalsRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 3" }}>Transfer Total</span>
          <span className={s.flightTotalVal}>{formatCurrency(p.costPrice || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(p.markup || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(p.vatTax || 0)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(p.sellingPrice || 0)}</span>
        </div>
      </div>

      <CancellationSummaryCard canc={canc} />
    </div>
  );
};

/* ─── Flight Pricing Group ───────────────────────────────────────────────── */

const ResetPriceIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
);

/**
 * Is this flight priced per direction?
 *
 * flightPricingMode is the intent; the stored rows are the fact, and they come
 * apart. Removing one direction of a split flight reverts the flag to FULL and
 * leaves the Outbound/Inbound rows in place, so a screen that trusts the flag
 * renders the full-flight shape, finds none of those keys, and shows blank
 * inputs over real money that is still in the booking total. Reading the rows
 * keeps what is stored reachable from the screen, which is what lets anyone
 * correct it.
 */
export const isSplitPricing = (product, pricing) => {
  if (product?.flightPricingMode === "SPLIT") return true;
  const prdKey = product?.id;
  if (!prdKey) return false;
  return ["Outbound", "Inbound"].some((dir) =>
    Object.keys(pricing || {}).some((k) => k.endsWith(`-${prdKey}-${dir}`))
  );
};

const FlightPricingGroup = ({ product, travelers, assignments, pricing, onChange, onCopyToAll, onAutoFillTickets, onResetPricing, vatMeta, vatLoading, canc }) => {
  const isSplit = isSplitPricing(product, pricing);
  // Both directions expanded by default; each can be collapsed independently.
  const [dirExpanded, setDirExpanded] = useState({ Outbound: true, Inbound: true });
  const toggleDir = (dir) => setDirExpanded((prev) => ({ ...prev, [dir]: !prev[dir] }));
  const [detailsOpen, setDetailsOpen] = useState({});
  const [showBreakdown, setShowBreakdown] = useState(false);

  const legs = product.legs || [];
  const outboundLegs = legs.filter((l) => l.direction === "Outbound");
  const inboundLegs = legs.filter((l) => l.direction === "Inbound");
  const tis = [...new Set(assignments.filter((a) => a.productId === product.id).map((a) => a.travelerIndex))];

  const computeDirTotals = (dir) => tis.reduce((acc, ti) => {
    const key = dir ? `${ti}-${product.id}-${dir}` : `${ti}-${product.id}`;
    const p = pricing[key] || {};
    acc.netPrice += p.netPrice || 0;
    acc.tax += p.tax || 0;
    acc.vatTax += p.vatTax || 0;
    acc.commission += p.commission || 0;
    acc.segFee += p.segFee || 0;
    acc.clientPrice += p.clientPrice || 0;
    return acc;
  }, { netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0, clientPrice: 0 });

  const outTotals = isSplit ? computeDirTotals("Outbound") : computeDirTotals(null);
  const inTotals = isSplit ? computeDirTotals("Inbound") : null;
  const grandTotals = isSplit ? {
    netPrice: outTotals.netPrice + inTotals.netPrice,
    tax: outTotals.tax + inTotals.tax,
    vatTax: outTotals.vatTax + inTotals.vatTax,
    commission: outTotals.commission + inTotals.commission,
    segFee: outTotals.segFee + inTotals.segFee,
    clientPrice: outTotals.clientPrice + inTotals.clientPrice,
  } : outTotals;

  const getSegInfo = (dirLegs) => {
    if (!dirLegs || dirLegs.length === 0) return null;
    const first = dirLegs[0];
    const last = dirLegs[dirLegs.length - 1];
    const dep = (first.time || "").slice(0, 5);
    const arr = (last.arrivalTime || "").slice(0, 5);
    const routeAirports = dirLegs.map((l) => l.departure || "—").concat([last.arrival || "—"]);
    return {
      departure: first.departure || "—",
      arrival: last.arrival || first.arrival || "—",
      route: routeAirports.join(" → "),
      date: formatDateShort(first.date),
      timeStr: dep && arr ? `${dep} – ${arr}` : dep || arr || "—",
      flightCode: [first.airlineCode, first.flightNumber].filter(Boolean).join("") || "—",
      cabinClass: first.cabinClass || "—",
      stops: dirLegs.length - 1,
    };
  };

  const toggleDetails = (key) => setDetailsOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderSegmentBar = (direction, dirLegs) => {
    const info = getSegInfo(dirLegs);
    if (!info) return null;
    const dirKey = direction || "full";

    const legPnr = dirLegs.map(l => l.pnr).find(Boolean) || "";
    const ownDirPnr = direction === "Outbound" ? product.outboundPnr : direction === "Inbound" ? product.inboundPnr : "";
    const dirPnr = legPnr
      || ownDirPnr
      || product.airlinePnr
      || (isSplit ? "" : (product.outboundPnr || product.inboundPnr || product.supplierRef))
      || "";

    return (
      <div className={s.segmentBar}>
        <div className={s.segmentBarInfo}>
          {direction && (
            <span className={`${s.segmentDirBadge} ${direction === "Outbound" ? s.segmentDirBadgeOut : s.segmentDirBadgeIn}`}>
              {direction.toUpperCase()}
            </span>
          )}
          <span className={s.segmentRoute}>{info.route}</span>
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentDate}>{info.date}</span>
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentTime}>{info.timeStr}</span>
          <span className={s.segmentSep}>|</span>
          <CopyableText text={info.flightCode} className={s.segmentCode} />
          {dirPnr && (
            <>
              <span className={s.segmentSep}>|</span>
              <span className={s.segmentPnrBadge}>PNR <CopyableText text={dirPnr} /></span>
            </>
          )}
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentCabin}>{info.cabinClass}</span>
        </div>
        <div className={s.segmentBarActions}>
          <button type="button" className={s.viewDetailsBtn} onClick={() => toggleDetails(dirKey)}>
            {detailsOpen[dirKey] ? "Hide" : "View"} Flight Details
            <ChevronIcon expanded={detailsOpen[dirKey]} />
          </button>
        </div>
      </div>
    );
  };

  const renderFlightDetails = (direction, dirLegs) => {
    const dirKey = direction || "full";
    if (!detailsOpen[dirKey] || !dirLegs || dirLegs.length === 0) return null;
    const first = dirLegs[0];
    const last = dirLegs[dirLegs.length - 1];
    const stops = dirLegs.length - 1;

    return (
      <div className={s.flightDetailsPanel}>
        <div className={s.fdRouteSection}>
          <div className={s.fdAirport}>
            <span className={s.fdAirportCode}>{first.departure || "—"}</span>
          </div>
          <div className={s.fdConnection}>
            <div className={s.fdConnectionLine} />
            <span className={s.fdConnectionLabel}>{stops === 0 ? "Direct" : `${stops} Stop${stops > 1 ? "s" : ""}`}</span>
            <div className={s.fdConnectionLine} />
          </div>
          <div className={s.fdAirport}>
            <span className={s.fdAirportCode}>{last.arrival || first.arrival || "—"}</span>
          </div>
        </div>
        <div className={s.fdMetaSection}>
          <div className={s.fdMetaItem}>
            <span className={s.fdMetaLabel}>Airline</span>
            <span className={s.fdMetaValue}>
              {first.airlineName || first.airlineCode || "—"}
              {first.airlineCode && first.airlineName && <span className={s.fdMetaCode}>({first.airlineCode})</span>}
            </span>
          </div>
          <div className={s.fdMetaItem}>
            <span className={s.fdMetaLabel}>Cabin</span>
            <span className={s.fdMetaValue}>{first.cabinClass || "Economy Class"}</span>
          </div>
          <div className={s.fdMetaItem}>
            <span className={s.fdMetaLabel}>Flight</span>
            <CopyableText text={[first.airlineCode, first.flightNumber].filter(Boolean).join("") || "—"} className={s.fdMetaValue} />
          </div>
          {(() => {
            const ownDetailPnr = direction === "Outbound" ? product.outboundPnr : direction === "Inbound" ? product.inboundPnr : "";
            const detailPnr = first.pnr
              || ownDetailPnr
              || product.airlinePnr
              || (isSplit ? "" : (product.outboundPnr || product.inboundPnr || product.supplierRef))
              || "";
            return detailPnr ? (
              <div className={s.fdMetaItem}>
                <span className={s.fdMetaLabel}>PNR</span>
                <CopyableText text={detailPnr} className={s.fdMetaValue} />
              </div>
            ) : null;
          })()}
          {product.supplierRef && (
            <div className={s.fdMetaItem}>
              <span className={s.fdMetaLabel}>Supplier Ref</span>
              <CopyableText text={product.supplierRef} className={s.fdMetaValue} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollapsedBar = (direction, dirLegs) => {
    const info = getSegInfo(dirLegs);
    if (!info) return null;
    const colLegPnr = dirLegs.map(l => l.pnr).find(Boolean) || "";
    const colOwnPnr = direction === "Outbound" ? product.outboundPnr : direction === "Inbound" ? product.inboundPnr : "";
    const colDirPnr = colLegPnr
      || colOwnPnr
      || product.airlinePnr
      || (isSplit ? "" : (product.outboundPnr || product.inboundPnr || product.supplierRef))
      || "";
    return (
      <div className={`${s.segmentBar} ${s.segmentBarCollapsed}`}>
        <div className={s.segmentBarInfo}>
          <span className={`${s.segmentDirBadge} ${direction === "Outbound" ? s.segmentDirBadgeOut : s.segmentDirBadgeIn}`}>
            {direction.toUpperCase()}
          </span>
          <span className={s.segmentRoute}>{info.route}</span>
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentDate}>{info.date}</span>
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentTime}>{info.timeStr}</span>
          <span className={s.segmentSep}>|</span>
          <CopyableText text={info.flightCode} className={s.segmentCode} />
          {colDirPnr && (
            <>
              <span className={s.segmentSep}>|</span>
              <span className={s.segmentPnrBadge}>PNR <CopyableText text={colDirPnr} /></span>
            </>
          )}
          <span className={s.segmentSep}>|</span>
          <span className={s.segmentCabin}>{info.cabinClass}</span>
        </div>
        <div className={s.segmentBarActions}>
          <button type="button" className={s.expandPricingBtn} onClick={() => toggleDir(direction)}>
            Expand {direction} Pricing
            <ChevronIcon expanded={false} />
          </button>
        </div>
      </div>
    );
  };

  const flightTypeLabel = product.flightType === "OW" ? "One Way" : product.flightType === "RT" ? "Round Trip" : product.flightType === "MC" ? "Multi-City" : "";

  return (
    <div className={s.groupCard}>
      {/* ── Header ── */}
      <div className={s.groupHeader}>
        <div className={`${s.groupIcon} ${s.groupIconFlight}`}>{PRODUCT_ICON_MAP.Flight || Icons.plane}</div>
        <div className={s.groupHeaderMain}>
          <div className={s.groupTitle}>
            {isSplit ? "Split" : "Full"} Flight Pricing
            {product.supplier && <span className={s.groupTitleSub}> — {product.supplier}</span>}
            {product.supplierRef && <span className={s.groupTitleSub}> · REF: <CopyableText text={product.supplierRef} /></span>}
          </div>
          <div className={s.groupSub}>
            {product.supplier && <>{product.supplier}</>}
            {product.supplier && <> · </>}
            {tis.length} Traveler{tis.length !== 1 ? "s" : ""}
            {flightTypeLabel && ` · ${flightTypeLabel}`}
            {` · Pricing Mode: ${isSplit ? "Split" : "Full"}`}
          </div>
        </div>
        <div className={s.groupHeaderRight}>
          {canc && <CancellationStatusPill status={canc.status} />}
          <span className={`${s.badge} ${s.badgePricingMode}`}>{isSplit ? "Split" : "Full"}</span>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <ProductCancellationOverview canc={canc} />
      {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
      <div className={canc?.cancelledAll ? cs.cancelledTableWrap : undefined}>

      {/* ── Summary Boxes ── */}
      <div className={s.pricingSummaryRow}>
        {isSplit ? (
          <>
            <div className={`${s.pricingSummaryBox} ${s.pricingSummaryBoxOut}`}>
              <span className={s.pricingSummaryBoxLabel}>OUTBOUND TOTAL</span>
              <span className={s.pricingSummaryBoxValue}>{formatCurrency(outTotals.clientPrice)}</span>
            </div>
            <div className={`${s.pricingSummaryBox} ${s.pricingSummaryBoxIn}`}>
              <span className={s.pricingSummaryBoxLabel}>INBOUND TOTAL</span>
              <span className={s.pricingSummaryBoxValue}>{formatCurrency(inTotals.clientPrice)}</span>
            </div>
            <div className={`${s.pricingSummaryBox} ${s.pricingSummaryBoxGrand}`}>
              <span className={s.pricingSummaryBoxLabel}>GRAND TOTAL</span>
              <span className={`${s.pricingSummaryBoxValue} ${s.pricingSummaryBoxValueAccent}`}>{formatCurrency(grandTotals.clientPrice)}</span>
            </div>
          </>
        ) : (
          <div className={`${s.pricingSummaryBox} ${s.pricingSummaryBoxGrand}`}>
            <span className={s.pricingSummaryBoxLabel}>FLIGHT TOTAL</span>
            <span className={`${s.pricingSummaryBoxValue} ${s.pricingSummaryBoxValueAccent}`}>{formatCurrency(outTotals.clientPrice)}</span>
          </div>
        )}
        {isSplit && (
          <div className={s.pricingSummaryToggle}>
            <button type="button" className={s.showBreakdownBtn} onClick={() => setShowBreakdown(!showBreakdown)}>
              {showBreakdown ? "Hide" : "Show"} Totals Breakdown
              <ChevronIcon expanded={showBreakdown} />
            </button>
          </div>
        )}
      </div>

      {/* ── Totals Breakdown ── */}
      {isSplit && showBreakdown && (
        <div className={s.breakdownGrid}>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel} />
            <span className={s.breakdownHead}>Net Price</span>
            <span className={s.breakdownHead}>Tax</span>
            <span className={s.breakdownHead}>VAT Tax</span>
            <span className={s.breakdownHead}>Commission</span>
            <span className={s.breakdownHead}>Seg. Fee</span>
            <span className={`${s.breakdownHead} ${s.breakdownHeadAccent}`}>Client Price</span>
          </div>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel}>Outbound</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(outTotals.clientPrice)}</span>
          </div>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel}>Inbound</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(inTotals.clientPrice)}</span>
          </div>
          <div className={`${s.breakdownRow} ${s.breakdownRowTotal}`}>
            <span className={s.breakdownLabel}>Grand Total</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(grandTotals.clientPrice)}</span>
          </div>
        </div>
      )}

      {/* ── Outbound/Inbound toggles (SPLIT only) — click to collapse/expand each ── */}
      {isSplit && (
        <div className={s.dirTabs}>
          <div className={s.dirTabsInner}>
            <button type="button" className={`${s.dirTab} ${dirExpanded.Outbound ? s.dirTabActive : ""}`} onClick={() => toggleDir("Outbound")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              Outbound
              <ChevronIcon expanded={dirExpanded.Outbound} />
            </button>
            <button type="button" className={`${s.dirTab} ${dirExpanded.Inbound ? s.dirTabActive : ""}`} onClick={() => toggleDir("Inbound")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
              Inbound
              <ChevronIcon expanded={dirExpanded.Inbound} />
            </button>
          </div>
        </div>
      )}

      {/* ── FULL mode: single section ── */}
      {!isSplit && (
        <>
          {renderSegmentBar(null, legs)}
          {renderFlightDetails(null, legs)}
          <FlightPricingTable
            travelers={travelers}
            assignments={assignments}
            pricing={pricing}
            productId={product.id}
            onChange={onChange}
            onCopyToAll={onCopyToAll}
            onAutoFillTickets={onAutoFillTickets}
            directionLabel={null}
            vatMeta={vatMeta}
            vatLoading={vatLoading}
          />
        </>
      )}

      {/* ── SPLIT mode: both directions, each independently expand/collapse ── */}
      {isSplit && ["Outbound", "Inbound"].map((dir) => {
        const dirLegs = dir === "Outbound" ? outboundLegs : inboundLegs;
        if (!dirLegs.length) return null;
        return (
          <Fragment key={dir}>
            {dirExpanded[dir] ? (
              <>
                {renderSegmentBar(dir, dirLegs)}
                {renderFlightDetails(dir, dirLegs)}
                <FlightPricingTable
                  travelers={travelers}
                  assignments={assignments}
                  pricing={pricing}
                  productId={product.id}
                  onChange={onChange}
                  onCopyToAll={onCopyToAll}
                  onAutoFillTickets={onAutoFillTickets}
                  directionLabel={dir}
                  vatMeta={vatMeta}
                  vatLoading={vatLoading}
                />
              </>
            ) : (
              renderCollapsedBar(dir, dirLegs)
            )}
          </Fragment>
        );
      })}

      {/* ── Grand Totals Footer (SPLIT mode) ── */}
      {isSplit && (
        <div className={s.grandTotalsFooter}>
          <div className={s.grandTotalsLeft}>
            <span className={s.grandTotalsTitle}>Grand Totals</span>
            <span className={s.grandTotalsSub}>(All in EUR)</span>
          </div>
          <div className={s.grandTotalsRight}>
            <div className={s.grandTotalCol}>
              <span className={s.grandTotalColLabel}>TOTAL NET PRICE</span>
              <span className={s.grandTotalColValue}>{formatCurrency(grandTotals.netPrice)}</span>
            </div>
            <div className={s.grandTotalCol}>
              <span className={s.grandTotalColLabel}>TOTAL TAX</span>
              <span className={s.grandTotalColValue}>{formatCurrency(grandTotals.tax)}</span>
            </div>
            <div className={s.grandTotalCol}>
              <span className={s.grandTotalColLabel}>TOTAL VAT TAX</span>
              <span className={s.grandTotalColValue}>{formatCurrency(grandTotals.vatTax)}</span>
            </div>
            <div className={s.grandTotalCol}>
              <span className={s.grandTotalColLabel}>TOTAL COMMISSION</span>
              <span className={`${s.grandTotalColValue} ${s.grandTotalColValueComm}`}>{formatCurrency(grandTotals.commission)}</span>
            </div>
            <div className={s.grandTotalCol}>
              <span className={s.grandTotalColLabel}>TOTAL SEG. FEE</span>
              <span className={s.grandTotalColValue}>{formatCurrency(grandTotals.segFee)}</span>
            </div>
            <div className={`${s.grandTotalCol} ${s.grandTotalColAccent}`}>
              <span className={s.grandTotalColLabel}>GRAND CLIENT PRICE</span>
              <span className={`${s.grandTotalColValue} ${s.grandTotalColValueAccent}`}>{formatCurrency(grandTotals.clientPrice)}</span>
            </div>
          </div>
        </div>
      )}
      </div>{/* /read-only wrap */}

      {(canc?.removedDirections || []).map((dir, i) => (
        <div key={i} style={{ padding: "0 20px 16px" }}>
          <CancelledDirectionBlock dir={dir} />
        </div>
      ))}

      <CancellationSummaryCard canc={canc} />
    </div>
  );
};

/* ─── Generic Pricing Group ──────────────────────────────────────────────── */

const GenericPricingGroup = ({ product, travelers, assignments, pricing, onChange, onCopyToAll, onResetPricing, vatMeta, vatLoading, canc }) => (
  <div className={s.groupCard}>
    <div className={s.groupHeader}>
      <div className={`${s.groupIcon} ${ICON_BG[product.type] || s.groupIconGeneric}`}>{PRODUCT_ICON_MAP[product.type] || Icons.box}</div>
      <div className={s.groupHeaderMain}>
        <div className={s.groupTitle}>
          {product.type}
          {product.supplier && <span className={s.groupTitleSub}> — {product.supplier}</span>}
          {product.supplierRef && <span className={s.groupTitleSub}> · REF: <CopyableText text={product.supplierRef} /></span>}
        </div>
        {product.supplier && (
          <div className={s.groupSub}>
            {product.supplier}
          </div>
        )}
      </div>
      <div className={s.groupHeaderRight}>
        {canc && <CancellationStatusPill status={canc.status} />}
        {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
      </div>
    </div>
    <ProductCancellationOverview canc={canc} />
    {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
    <div className={canc?.cancelledAll ? cs.cancelledTableWrap : undefined}>
      <PricingTable travelers={travelers} assignments={assignments} pricing={pricing} productId={product.id} onChange={onChange} onCopyToAll={onCopyToAll} vatMeta={vatMeta} vatLoading={vatLoading} />
    </div>
    <CancellationSummaryCard canc={canc} />
  </div>
);

/* ─── SSR Pricing Section ────────────────────────────────────────────────── */

const SSRPricingSection = ({ ssrItems, pricing, onChange, vatMeta, vatLoading }) => {
  if (!ssrItems || ssrItems.length === 0) return null;

  const getSSRKey = (ssr, idx) => `ssr-${ssr.itemId}-${ssr.travelerId ?? idx}`;

  const ssrTotal = ssrItems.reduce((acc, ssr, idx) => {
    const p = pricing[getSSRKey(ssr, idx)] || {};
    return acc + ((p.sellingPrice || 0) * (ssr.qty || 1));
  }, 0);

  const ssrTotalCost = ssrItems.reduce((acc, ssr, idx) => {
    const p = pricing[getSSRKey(ssr, idx)] || {};
    return acc + ((p.costPrice || 0) * (ssr.qty || 1));
  }, 0);

  const ssrTotalVatTax = ssrItems.reduce((acc, ssr, idx) => {
    const p = pricing[getSSRKey(ssr, idx)] || {};
    return acc + ((p.vatTax || 0) * (ssr.qty || 1));
  }, 0);

  const ssrTotalMarkup = ssrItems.reduce((acc, ssr, idx) => {
    const p = pricing[getSSRKey(ssr, idx)] || {};
    return acc + ((p.markup || 0) * (ssr.qty || 1));
  }, 0);

  const getTravName = (ssr) => {
    const t = ssr.travelerInformation;
    if (t) {
      const n = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ");
      if (n) return n;
    }
    return ssr.travelerLabel || "—";
  };
  const getTravDOB = (ssr) => ssr.travelerInformation?.dob ? formatDOB(ssr.travelerInformation.dob) : "—";
  const getTravGender = (ssr) => {
    const g = ssr.travelerInformation?.gender;
    if (!g) return "—";
    const u = g.toUpperCase();
    if (u === "MALE" || u === "M") return "Male";
    if (u === "FEMALE" || u === "F") return "Female";
    return g;
  };
  const getTravType = (ssr) => ssr.travelerInformation?.dob ? (PAX_TYPE_MAP[classifyTraveler(ssr.travelerInformation.dob)] || "ADT") : "—";

  return (
    <div className={s.groupCard}>
      <div className={s.groupHeader}>
        <div className={`${s.groupIcon} ${s.groupIconSSR}`}><SSRIcon /></div>
        <div className={s.groupHeaderMain}>
          <div className={s.groupTitle}>SSR Services</div>
          <div className={s.groupSub}>
            {ssrItems.length} item{ssrItems.length !== 1 ? "s" : ""} · Per-traveler pricing
          </div>
        </div>
        {ssrTotal > 0 && (
          <div className={s.groupHeaderRight}>
            <span className={s.ssrHeaderValue}>{formatCurrency(ssrTotal)}</span>
          </div>
        )}
      </div>

      <div className={s.ssrTable}>
        <div className={s.ssrTableHead}>
          <span className={s.fth}>SSR Item</span>
          <span className={s.fth}>Code</span>
          <span className={s.fth}>Type</span>
          <span className={s.fth}>Traveler</span>
          <span className={s.fth}>Gender</span>
          <span className={s.fth}>DOB</span>
          <span className={s.fth} style={{ textAlign: "center" }}>Qty</span>
          <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
          <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
          <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
          <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
        </div>

        <div className={s.ssrTableBody}>
          {ssrItems.map((ssr, idx) => {
            const key = getSSRKey(ssr, idx);
            const p   = pricing[key] || { costPrice: 0, sellingPrice: 0, markup: 0, vatTax: 0 };
            return (
              <div key={`${ssr.itemId}-${ssr.travelerId ?? idx}`} className={s.ssrRow}>
                <div className={s.ssrCellLabel}>
                  <span className={s.ssrLabelText}>{ssr.label}</span>
                </div>
                <div className={s.fCell}><span className={s.ssrCodeBadge}>{ssr.code}</span></div>
                <div className={s.fCell}><span className={`${s.cellType} ${TYPE_CLS[getTravType(ssr)] || s.typeUnknown}`}>{getTravType(ssr)}</span></div>
                <div className={`${s.fCell} ${s.fCellName}`}><span className={s.travName}>{getTravName(ssr)}</span></div>
                <div className={s.fCell}><span className={s.genderPill}>{getTravGender(ssr)}</span></div>
                <div className={s.fCell}><span className={s.dobText}>{getTravDOB(ssr)}</span></div>
                <div className={s.fCell} style={{ justifyContent: "center" }}><span className={s.ssrQtyBadge}>×{ssr.qty || 1}</span></div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
                </div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
                </div>
                <div className={s.fCell}>
                  <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
                </div>
                <div className={s.fCell}>
                  <span className={s.clientPriceVal}>{formatCurrency((p.sellingPrice || 0) * (ssr.qty || 1))}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={s.ssrTotalsRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 8" }}>Total</span>
          <span className={s.flightTotalVal}>{formatCurrency(ssrTotalCost)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(ssrTotalMarkup)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(ssrTotalVatTax)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(ssrTotal)}</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Main StepPricing ───────────────────────────────────────────────────── */

const StepPricing = ({ data, onChange, onCalculateVAT, hideSummary }) => {
  const pricing  = data.pricing  || {};
  const ssrItems = data.ssrItems || [];
  const [vatMeta, setVatMeta] = useState({});
  const [vatLoading, setVatLoading] = useState({});
  const vatTimers = useRef({});
  const pricingRef = useRef(pricing);
  pricingRef.current = pricing;

  const findProductForKey = useCallback((key) => {
    if (key.startsWith("hotelroom-")) {
      const match = key.match(/^hotelroom-(PRD-\d+)/);
      return match ? (data.products || []).find((p) => p.id === match[1]) : null;
    }
    if (key.startsWith("ssr-")) return null;
    const prdMatch = key.match(/(PRD-\d+)/);
    if (prdMatch) return (data.products || []).find((p) => p.id === prdMatch[1]);
    return null;
  }, [data.products]);

  const triggerVATCalculation = useCallback((key, updatedEntry) => {
    if (!onCalculateVAT) return;

    if (vatTimers.current[key]) clearTimeout(vatTimers.current[key]);

    vatTimers.current[key] = setTimeout(async () => {
      const isHotelRoom = key.startsWith("hotelroom-");
      const isSSR = key.startsWith("ssr-");

      let payload;
      if (isSSR) {
        const ssrItemId = key.split("-")[1];
        const ssr = ssrItems.find((s) => String(s.itemId) === ssrItemId);
        if (!ssr?._serverId) return;
        payload = {
          ssrId: ssr._serverId,
          markup: updatedEntry.markup || 0,
          cost_price: updatedEntry.costPrice || 0,
          selling_price: updatedEntry.sellingPrice || 0,
        };
      } else {
        const product = findProductForKey(key);
        if (!product?._serverId) return;

        if (product.type === "Flight") {
          const dirMatch = key.match(/-(Outbound|Inbound)$/);
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.netPrice || 0,
            tax: updatedEntry.tax || 0,
            commission: updatedEntry.commission || 0,
            seg_fee: updatedEntry.segFee || 0,
            bound: dirMatch ? dirMatch[1].toLowerCase() : "outbound",
          };
        } else if (product.type === "Hotel") {
          payload = {
            productId: product._serverId,
            room_net_price: updatedEntry.netPrice || 0,
            markup: updatedEntry.markup || 0,
          };
        } else if (product.type === "Transfer") {
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.costPrice || 0,
            commission: updatedEntry.markup || 0,
          };
        } else {
          // Generic products (Car Rental, Insurance, Visa, Package, Cruise, Incoming) —
          // VAT is computed server-side on the margin (markup) per product type.
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.costPrice || 0,
            markup: updatedEntry.markup || 0,
          };
        }
      }

      setVatLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const result = await onCalculateVAT(payload);
        if (result.success && result.data) {
          const vatAmount = result.data.vat_amount || 0;
          setVatMeta((prev) => ({ ...prev, [key]: result.data }));
          const latestPricing = pricingRef.current;
          const cur = latestPricing[key] || defaultPricingEntry();
          const upd = { ...cur, vatTax: vatAmount };
          if (isHotelRoom) {
            upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + vatAmount;
          } else if (key.startsWith("ssr-")) {
            // SSR Fee is VAT-inclusive: VAT is embedded inside the markup (fee),
            // never added on top. Customer total = cost + fee.
            upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0);
          } else if (findProductForKey(key)?.type === "Flight") {
            upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + vatAmount + (upd.commission || 0) + (upd.segFee || 0);
            upd.sellingPrice = upd.clientPrice;
          } else {
            upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0) + vatAmount;
          }
          onChange("pricing", { ...latestPricing, [key]: upd });
        }
      } finally {
        setVatLoading((prev) => ({ ...prev, [key]: false }));
      }
    }, 600);
  }, [onCalculateVAT, data.products, ssrItems, findProductForKey, onChange]);

  const updatePricing = (key, field, value) => {
    const cur = pricing[key] || defaultPricingEntry();
    const upd = { ...cur };

    if (field === "ticketNumber") {
      upd.ticketNumber = value;
    } else {
      upd[field] = parseFloat(value) || 0;
    }

    const isHotelRoom = key.startsWith("hotelroom-");

    if (["netPrice", "tax", "commission", "segFee"].includes(field)) {
      if (isHotelRoom) {
        upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      } else {
        upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + (upd.vatTax || 0) + (upd.commission || 0) + (upd.segFee || 0);
        upd.sellingPrice = upd.clientPrice;
      }
    }

    if (field === "costPrice" || field === "markup") {
      const isSSR = key.startsWith("ssr-");
      upd.sellingPrice = isHotelRoom
        ? (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0)
        : isSSR
          // SSR Fee is VAT-inclusive: VAT is embedded inside the fee, not added on top.
          ? (upd.costPrice || 0) + (upd.markup || 0)
          : (upd.costPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
    }

    onChange("pricing", { ...pricing, [key]: upd });

    const vatTriggerFields = isHotelRoom
      ? ["netPrice", "markup"]
      : key.startsWith("ssr-")
        ? ["costPrice", "markup"]
        : ["netPrice", "tax", "commission", "segFee", "costPrice", "markup"];
    if (vatTriggerFields.includes(field)) {
      triggerVATCalculation(key, upd);
    }
  };

  const resetProductPricing = useCallback((productId) => {
    const latest = pricingRef.current;
    const newPricing = { ...latest };
    Object.keys(newPricing).forEach((key) => {
      if (key.includes(productId)) {
        const cur = newPricing[key];
        const zeroed = {};
        Object.keys(cur).forEach((f) => {
          zeroed[f] = f === "ticketNumber" ? cur[f] : 0;
        });
        newPricing[key] = zeroed;
      }
    });
    onChange("pricing", newPricing);
  }, [onChange]);

  // Copy entered prices from one traveler to every other traveler in the same product.
  // Does a single batched state update so the closure sees fresh data, then schedules
  // VAT recalculation for each target so backend rates stay correct.
  const copyPricingToAll = useCallback((sourceKey, targetKeys, fields) => {
    const latest = pricingRef.current;
    const src = latest[sourceKey] || defaultPricingEntry();
    const newPricing = { ...latest };

    targetKeys.forEach((tk) => {
      if (tk === sourceKey) return;
      const cur = newPricing[tk] || defaultPricingEntry();
      const upd = { ...cur };
      fields.forEach((field) => { upd[field] = src[field] || 0; });

      // Recompute the derived price using the same rules as updatePricing.
      const isHotelRoom = tk.startsWith("hotelroom-");
      const isSSR = tk.startsWith("ssr-");
      if (isHotelRoom) {
        upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      } else if (isSSR) {
        // SSR Fee is VAT-inclusive: VAT is embedded inside the fee, not added on top.
        upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0);
      } else if (findProductForKey(tk)?.type === "Flight") {
        upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + (upd.vatTax || 0) + (upd.commission || 0) + (upd.segFee || 0);
        upd.sellingPrice = upd.clientPrice;
      } else {
        upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      }

      newPricing[tk] = upd;
    });

    onChange("pricing", newPricing);

    // Trigger VAT recalc for every target so backend rates re-evaluate.
    targetKeys.forEach((tk) => {
      if (tk === sourceKey) return;
      triggerVATCalculation(tk, newPricing[tk]);
    });
  }, [onChange, findProductForKey, triggerVATCalculation]);

  const autoFillTickets = (productId, directionLabel, masterTicket, travelerIndices) => {
    const updated = { ...pricing };
    travelerIndices.forEach((ti, idx) => {
      const key = directionLabel ? `${ti}-${productId}-${directionLabel}` : `${ti}-${productId}`;
      const cur = updated[key] || defaultPricingEntry();
      updated[key] = { ...cur, ticketNumber: idx === 0 ? masterTicket : incrementTicket(masterTicket, idx) };
    });
    onChange("pricing", updated);
  };

  /* Stable key: re-run ticket pre-fill when ticket fields OR assignments change */
  const flightTicketsKey = useMemo(
    () => JSON.stringify({
      tickets: (data.products || [])
        .filter((p) => p.type === "Flight")
        .map((p) => ({
          id: p.id,
          mode: p.flightPricingMode,
          t: p.ticketNumber || "",
          o: p.outboundTicket || "",
          i: p.inboundTicket || "",
        })),
      assigns: (data.assignments || []).map((a) => `${a.productId}:${a.travelerIndex}`),
    }),
    [data.products, data.assignments]
  );

  /* Pre-fill flight ticket numbers — supports full + split pricing. Only fills empty slots. */
  useEffect(() => {
    const flightProducts = (data.products || []).filter(
      (p) => p.type === "Flight" &&
        (p.ticketNumber || p.outboundTicket || p.inboundTicket)
    );
    if (flightProducts.length === 0) return;

    const updated = { ...(data.pricing || {}) };
    let changed = false;

    flightProducts.forEach((product) => {
      const isSplit = isSplitPricing(product, data.pricing);
      const tis = [
        ...new Set(
          (data.assignments || [])
            .filter((a) => a.productId === product.id)
            .map((a) => a.travelerIndex)
        ),
      ];
      if (tis.length === 0) return;

      const directions = isSplit ? ["Outbound", "Inbound"] : [null];
      directions.forEach((dir) => {
        const masterTicket = isSplit
          ? (dir === "Outbound" ? product.outboundTicket : product.inboundTicket)
          : product.ticketNumber;
        if (!masterTicket) return;

        tis.forEach((ti, idx) => {
          const key = dir
            ? `${ti}-${product.id}-${dir}`
            : `${ti}-${product.id}`;
          const cur = updated[key] || defaultPricingEntry();
          if (!cur.ticketNumber) {
            const ticket = idx === 0 ? masterTicket : incrementTicket(masterTicket, idx);
            updated[key] = { ...cur, ticketNumber: ticket };
            changed = true;
          }
        });
      });
    });

    if (changed) onChange("pricing", updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightTicketsKey]);

  /* Summary totals — count ONLY pricing entries that still map to a current
     product or SSR. Removing a product/SSR leaves its pricing entries behind in
     `data.pricing`; without this filter those orphans keep inflating the totals
     ("financial details of previous booking still there"). */
  const livePricedProductIds = new Set((data.products || []).map((p) => p.id));
  const liveSsrItemIds = new Set((data.ssrItems || []).map((sx) => String(sx.itemId)));
  const isLivePricingKey = (key) => {
    if (key.startsWith("ssr-")) return liveSsrItemIds.has(key.split("-")[1]);
    const m = key.match(/PRD-\d+/);
    return m ? livePricedProductIds.has(m[0]) : false;
  };
  const allValues    = Object.entries(pricing).filter(([k]) => isLivePricingKey(k)).map(([, v]) => v);
  const totalSelling = allValues.reduce((acc, p) => acc + (p.sellingPrice || p.clientPrice || 0), 0);
  const totalCost    = allValues.reduce((acc, p) => acc + (p.costPrice || ((p.netPrice || 0) + (p.tax || 0))), 0);
  const totalMargin  = totalSelling - totalCost;
  const adjustmentsTotal = (data.adjustments || []).reduce((sum, a) => {
    const val = a.mode === "Fixed" ? (a.value || 0) : (totalSelling * (a.value || 0)) / 100;
    return sum + (a.type === "Discount" ? -val : val);
  }, 0);
  const grandTotal = (data.bookingGrandTotal > 0) ? data.bookingGrandTotal : totalSelling + adjustmentsTotal;
  const marginPct  = totalSelling > 0 ? ((totalMargin / totalSelling) * 100).toFixed(1) : "0.0";

  const hotelProducts = (data.products || []).filter((p) => p.type === "Hotel");
  const hasHotels = hotelProducts.length > 0;
  const transferProducts = (data.products || []).filter((p) => p.type === "Transfer");
  const hasTransfers = transferProducts.length > 0;

  const hasFlightsOrGeneric = (data.products || []).some((p) => p.type !== "Hotel" && p.type !== "Transfer");
  if ((data.assignments || []).length === 0 && ssrItems.length === 0 && !hasHotels && !hasTransfers && !hasFlightsOrGeneric) return (
    <div className={styles.stepContent}>
      <div className={s.emptyWrap}>
        <div className={s.emptyIcon}>{Icons.wallet}</div>
        <div className={s.emptyTitle}>No assignments to price</div>
        <div className={s.emptyDesc}>Assign travelers to products first, then set pricing per traveler per product.</div>
      </div>
    </div>
  );

  /* Per-traveler groupings (exclude hotels and transfers — those render as fixed-price cards) */
  const grouped = {};
  (data.assignments || []).forEach((a) => {
    const prod = (data.products || []).find((p) => p.id === a.productId);
    if (!prod || prod.type === "Hotel" || prod.type === "Transfer") return;
    if (!grouped[a.productId]) grouped[a.productId] = [];
    if (!grouped[a.productId].includes(a.travelerIndex)) grouped[a.productId].push(a.travelerIndex);
  });
  // Also show Flight/Generic products that have no assignments yet (e.g. copied products)
  (data.products || []).forEach((prod) => {
    if (prod.type === "Hotel" || prod.type === "Transfer") return;
    if (!grouped[prod.id]) grouped[prod.id] = [];
  });

  const hasPackage = (data.products || []).some((p) => p.type === "Package");
  const PRODUCT_ORDER = hasPackage
    ? ["Package", "Transfer", "Insurance", "CarRental", "Visa", "Cruise", "Incoming", "Flight", "Hotel"]
    : ["Flight", "Hotel", "Transfer", "Insurance", "CarRental", "Visa", "Cruise", "Incoming", "Package"];
  const productOrder = (type) => { const i = PRODUCT_ORDER.indexOf(type); return i >= 0 ? i : 99; };

  return (
    <div className={styles.stepContent}>
      {!hideSummary && (
        <>
          {/* ── Summary Tiles ── */}
          <div className={s.summaryBar}>
            <div className={s.tile}>
              <span className={s.tileLabel}>Total Selling</span>
              <span className={s.tileValue}>{formatCurrency(totalSelling)}</span>
            </div>
            <div className={s.tile}>
              <span className={s.tileLabel}>Total Cost</span>
              <span className={s.tileValue}>{formatCurrency(totalCost)}</span>
            </div>
            <div className={s.tile}>
              <div className={s.tileLabelRow}>
                <span className={s.tileLabel}>Margin</span>
                <span className={`${s.tilePercent} ${totalMargin >= 0 ? s.tilePercentPos : s.tilePercentNeg}`}>
                  {totalMargin >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                  {marginPct}%
                </span>
              </div>
              <span className={`${s.tileValue} ${totalMargin >= 0 ? s.tileValuePositive : s.tileValueNegative}`}>{formatCurrency(totalMargin)}</span>
            </div>
            <div className={`${s.tile} ${s.tileAccent}`}>
              <span className={s.tileLabel}>Grand Total</span>
              <span className={`${s.tileValue} ${s.tileValueAccent}`}>{formatCurrency(grandTotal)}</span>
            </div>
          </div>

          {adjustmentsTotal !== 0 && (
            <div className={s.infoStrip}>
              <InfoIcon />
              Adjustments: {formatCurrency(adjustmentsTotal)} · Outstanding: {formatCurrency(grandTotal)}
            </div>
          )}
        </>
      )}

      {(() => {
        const allSorted = (data.products || [])
          .slice()
          .sort((a, b) => productOrder(a.type) - productOrder(b.type));
        let accIdx = 0;
        const cancMap = data.cancellation || {};
        return allSorted.map((product) => {
          const canc = cancMap[product.id] || null;
          if (product.type === "Hotel") {
            accIdx += 1;
            return (
              <HotelPricingGroup
                key={product.id}
                product={product}
                pricing={pricing}
                onChange={updatePricing}
                onResetPricing={() => resetProductPricing(product.id)}
                accommodationIndex={accIdx}
                vatMeta={vatMeta}
                vatLoading={vatLoading}
                canc={canc}
              />
            );
          }
          if (product.type === "Transfer") {
            return (
              <TransferPricingGroup
                key={product.id}
                product={product}
                pricing={pricing}
                onChange={updatePricing}
                onResetPricing={() => resetProductPricing(product.id)}
                vatMeta={vatMeta}
                vatLoading={vatLoading}
                canc={canc}
              />
            );
          }
          if (product.type === "Flight") {
            return <FlightPricingGroup key={product.id} product={product} travelers={data.travelers} assignments={data.assignments} pricing={pricing} onChange={updatePricing} onCopyToAll={copyPricingToAll} onAutoFillTickets={autoFillTickets} onResetPricing={() => resetProductPricing(product.id)} vatMeta={vatMeta} vatLoading={vatLoading} canc={canc} />;
          }
          return <GenericPricingGroup key={product.id} product={product} travelers={data.travelers} assignments={data.assignments} pricing={pricing} onChange={updatePricing} onCopyToAll={copyPricingToAll} onResetPricing={() => resetProductPricing(product.id)} vatMeta={vatMeta} vatLoading={vatLoading} canc={canc} />;
        });
      })()}

      <SSRPricingSection ssrItems={ssrItems} pricing={pricing} onChange={updatePricing} vatMeta={vatMeta} vatLoading={vatLoading} />

      {data.saleType === "PACKAGE_TRAVEL" && (
        <div className={s.infoStrip}>
          <InfoIcon />
          Package Travel VAT: Margin-based VAT will be calculated automatically based on the total margin.
        </div>
      )}
    </div>
  );
};

export default StepPricing;